/**
 * Section "bubble" — the card-wrapped section the Today screen uses,
 * lifted into one shared component so every screen delineates its
 * sections the same way. Before this, three screens (household, admin,
 * you) each defined an identical local `Section`, and others (notably
 * Grow/walk) left sections bare with no bubble at all.
 *
 * A bubble is `.card` + `p-5`, with an optional header. The header
 * supports a serif `title`, an italic `subtitle`, a small `kicker`
 * label, and a right-aligned `action` (e.g. an IconButton), covering
 * both the plain settings sections and the richer Grow headers.
 *
 * Margin (`mb-4`) is baked in so it's a drop-in for the screens that
 * stacked sections with per-section spacing; inside a `space-y-*`
 * container the adjacent margins simply collapse.
 */
import type { ReactNode } from 'react';

interface SectionProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Small uppercase label shown above the title. */
  kicker?: ReactNode;
  /** Right-aligned header slot (e.g. an IconButton). */
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function Section({
  title,
  subtitle,
  kicker,
  action,
  className = '',
  children,
}: SectionProps) {
  const hasHeader = !!(title || subtitle || kicker || action);
  return (
    <section className={`card mb-4 p-5 ${className}`}>
      {hasHeader && (
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            {kicker && <div className="kicker text-sage">{kicker}</div>}
            {title && (
              <h2 className="font-serif text-xl leading-tight text-ink">{title}</h2>
            )}
            {subtitle && <p className="mt-1 font-serif italic text-soft">{subtitle}</p>}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
      {children}
    </section>
  );
}
